import {Component, EventEmitter, Output, computed, input, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {AiSuggestion, AiWorkResponse, Tone} from '../../models/ai-suggestion-model';

/**
 * The detail pane (right). Dumb/presentational: given a suggestion + its AI work,
 * it renders EVERY field the API exposes — identity, confidence, rationale,
 * version bundle, engineer flags, diff, checksum proof, statuses and the full
 * AI decision log — and emits approve / reject(reason) / download.
 */
@Component({
    selector: 'app-ai-suggestion-detail',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './ai-suggestion-detail.component.html',
    styleUrl: './ai-suggestion-detail.component.css'
})
export class AiSuggestionDetailComponent {

    suggestion = input<AiSuggestion | null>(null);
    aiWork = input<AiWorkResponse | null>(null);
    loading = input<boolean>(false);
    busy = input<boolean>(false);

    @Output() approve = new EventEmitter<void>();
    @Output() reject = new EventEmitter<string>();
    @Output() download = new EventEmitter<void>();

    rejecting = signal<boolean>(false);
    rejectReason = '';
    showRawDiff = signal<boolean>(false);
    showRawChecksum = signal<boolean>(false);
    private openSteps = signal<Set<number>>(new Set<number>());

    diff = computed<any>(() => this.parse(this.suggestion()?.diffJson ?? null));
    checksum = computed<any>(() => this.parse(this.suggestion()?.checksumProofJson ?? null));

    isPending = computed<boolean>(() => (this.suggestion()?.approvalStatus ?? '').toUpperCase() === 'PENDING');

    versions = computed<string[]>(() => {
        const v = this.suggestion()?.versionNames;
        if (!v) {
            return [];
        }
        return v.split(/[,+]/).map(x => x.trim()).filter(x => x.length > 0);
    });

    engineerFlags = computed<string[]>(() => {
        const d = this.diff();
        const raw = d?.engineerFlags ?? d?.flags ?? null;
        if (Array.isArray(raw)) {
            return raw.map((x: any) => typeof x === 'string' ? x : this.pretty(x));
        }
        if (typeof raw === 'string' && raw.trim()) {
            return [raw];
        }
        return [];
    });

    bytesChanged = computed<number | null>(() => {
        const d = this.diff();
        const n = d?.bytesChanged ?? d?.bytes ?? d?.changedBytes ?? null;
        return typeof n === 'number' ? n : null;
    });

    transfers = computed<string[]>(() => {
        const d = this.diff();
        const raw = d?.transfersApplied ?? d?.transferLog ?? d?.transfers ?? null;
        if (Array.isArray(raw)) {
            return raw.map((x: any) => typeof x === 'string' ? x : this.pretty(x));
        }
        if (typeof raw === 'string' && raw.trim()) {
            return raw.split('|').map(x => x.trim()).filter(x => x.length > 0);
        }
        return [];
    });

    confidencePct = computed<number | null>(() => {
        const c = this.suggestion()?.confidence;
        if (c == null) {
            return null;
        }
        const pct = c <= 1 ? c * 100 : c;
        return Math.round(pct);
    });

    confidenceTone = computed<Tone>(() => {
        const p = this.confidencePct();
        if (p == null) {
            return 'neutral';
        }
        if (p >= 65) {
            return 'success';
        }
        if (p >= 35) {
            return 'warning';
        }
        return 'destructive';
    });

    model = computed<string | null>(() => {
        const steps = this.aiWork()?.steps ?? [];
        for (let i = steps.length - 1; i >= 0; i--) {
            if (steps[i].model) {
                return steps[i].model;
            }
        }
        return null;
    });

    totalTokens = computed<number | null>(() => {
        const steps = this.aiWork()?.steps ?? [];
        let total = 0;
        let any = false;
        for (const s of steps) {
            if (typeof s.tokens === 'number') {
                total += s.tokens;
                any = true;
            }
        }
        return any ? total : null;
    });

    /* ---- formatting helpers ---- */

    parse(json: string | null): any {
        if (!json) {
            return null;
        }
        try {
            return JSON.parse(json);
        } catch {
            return null;
        }
    }

    pretty(value: any): string {
        try {
            return JSON.stringify(value, null, 2);
        } catch {
            return String(value);
        }
    }

    isObject(value: any): boolean {
        return value !== null && typeof value === 'object';
    }

    approvalTone(status: string | null | undefined): Tone {
        switch ((status ?? '').toUpperCase()) {
            case 'APPROVED':
                return 'success';
            case 'REJECTED':
                return 'destructive';
            case 'PENDING':
                return 'warning';
            default:
                return 'neutral';
        }
    }

    processingTone(status: string | null | undefined): Tone {
        switch ((status ?? '').toUpperCase()) {
            case 'DONE':
            case 'COMPLETED':
                return 'success';
            case 'PROCESSING':
                return 'info';
            case 'ERROR':
            case 'FAILED':
                return 'destructive';
            default:
                return 'neutral';
        }
    }

    phaseTone(phase: string | null | undefined): Tone {
        switch ((phase ?? '').toUpperCase()) {
            case 'VERIFY':
            case 'GATE':
                return 'info';
            case 'CHECKSUM':
            case 'PACKAGE':
            case 'EXPORT':
                return 'success';
            case 'DTC':
                return 'warning';
            default:
                return 'neutral';
        }
    }

    /* ---- reject inline panel ---- */

    startReject(): void {
        this.rejecting.set(true);
    }

    cancelReject(): void {
        this.rejecting.set(false);
        this.rejectReason = '';
    }

    confirmReject(): void {
        this.reject.emit(this.rejectReason.trim());
        this.rejecting.set(false);
        this.rejectReason = '';
    }

    /* ---- step expansion ---- */

    toggleStep(seq: number): void {
        const next = new Set(this.openSteps());
        if (next.has(seq)) {
            next.delete(seq);
        } else {
            next.add(seq);
        }
        this.openSteps.set(next);
    }

    isStepOpen(seq: number): boolean {
        return this.openSteps().has(seq);
    }
}
